<template>
  <div class="addServiceForm divMarginTop" ref="imageUploadRef">
    <form ref="formRef" @submit.prevent="addOrUpdateService">
      <!-- Step 1: Initial Form -->
      <div
        class="fullWidth column alignCentre"
        v-show="currentStep === 'initial'"
      >
        <div class="fullWidth">
          <label for="itemName">Service name</label>
          <input
            class="fullWidth alignCentre"
            type="text"
            id="itemName"
            v-model="itemName"
            required
            :class="{ 'highlight-input': highlightName }"
          />
        </div>
        <div class="fullWidth">
          <label for="itemDescription">Service description</label>
          <textarea
            class="fullWidth"
            id="itemDescription"
            v-model="itemDescription"
            required
            :class="{ 'highlight-input': highlightDescription }"
          ></textarea>
        </div>
        <div class="fullWidth">
          <ImageUpload
            @updateImages="handleImageUpdates"
            @updateThumbnail="updateThumbnailIndex"
            :newImages="selectedFiles"
            :existingImages="existingImageUrls"
            v-model:thumbnailIndex="thumbnailIndex"
          />
        </div>
        <div class="fullWidth">
          <label for="price">Price</label>
          <input
            type="number"
            id="price"
            v-model.number="price"
            required
            class="form-control alignCentre"
            :class="{ 'highlight-input': highlightPrice }"
            @blur="validatePrice"
          />
        </div>
        <div class="fullWidth">
          <label for="category">Category (optional)</label>
          <select id="category" v-model="category" class="form-control">
            <option v-for="cat in categories" :key="cat" :value="cat">
              {{ cat }}
            </option>
          </select>
        </div>
        <div class="row alignCentreRow fullWidth justifyToStartEnd">
          <ToggleSwitch
            label="Location"
            v-model="showMap"
            :justifyToStartEnd="true"
            :hideStatusText="true"
          >
            <template v-slot:tooltip>
              <HelpHint
                :message="'Toggle ON to allow customers to see where the service is located. Toggle OFF to hide the location.'"
              />
            </template>
          </ToggleSwitch>
          <!-- <ToggleSwitch
            label="Public"
            v-model="isPublished"
            :justifyToEnd="true"
            :hideStatusText="true"
          >
            <template v-slot:tooltip>
              <HelpHint
                :message="'Toggle ON to publish the service to the public. Toggle OFF to keep the service as a draft and publish later. You can change this later.'"
              />
            </template>
          </ToggleSwitch> -->
        </div>
        <SetLocation
          :showMap="showMap"
          :lastPickedLocation="location"
          @update-location="updateLocation"
        />
      </div>
      <!-- Step 2: Availability -->
      <div
        class="fullWidth alignCentre"
        v-show="currentStep === 'availability'"
      >
        <p class="nomargintop">Select regular availability</p>
        <div class="weekday-container column noGap">
          <div
            v-for="(day, index) in daysOfWeek.slice(0, 5)"
            :key="index"
            @click="selectDay(index)"
            :class="['innerSticker', { accentSelection: selectedDays[index] }]"
          >
            {{ day }}
          </div>
        </div>
        <div class="weekend-container" style="margin-top: var(--space-4)">
          <div
            v-for="(day, index) in daysOfWeek.slice(5)"
            :key="index + 5"
            @click="selectDay(index + 5)"
            :class="['innerSticker', { accentSelection: selectedDays[index + 5] }]"
          >
            {{ day }}
          </div>
        </div>
      </div>
      <!-- Step 3: Customization -->
      <div class="fullWidth" v-show="currentStep === 'customization'">
        <p class="nomargintopbottom alignCentre">
          Choose your availability for bookings<br />
          <small class="nomargintop alignCentre gray">
            To show availability at all times, no need to click on all slots,
            just leave the full column empty.
          </small>
        </p>
        <table class="selectable">
          <thead>
            <tr>
              <th v-for="(day, index) in selectedDaysOfWeek" :key="index">
                {{ day }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="hour in hours" :key="hour">
              <td
                v-for="(day, index) in selectedDaysOfWeek"
                :key="index"
                @click="toggleSlot(index, hour)"
                :class="{
                  'selected-slot': isSlotSelected(index, hour),
                  innerSticker: isSlotSelected(index, hour),
                  accentSelection: isSlotSelected(index, hour),
                }"
              >
                {{ hour }}
              </td>
            </tr>
          </tbody>
        </table>
        <p class="nomargintopbottom alignCentre"></p>
      </div>
      <!-- Step 4: Preview -->
      <PreviewItem
        v-show="currentStep === 'preview'"
        :productData="gatheredServiceData"
        :coinSymbol="coinSymbol"
        :thumbnailIndex="thumbnailIndex"
        :userUnitsOrg="userUnitsOrg"
      />
    </form>
  </div>
</template>

<script>
import { useStore } from "vuex";
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  reactive,
  watchEffect,
} from "vue";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  setDoc,
  getDocs,
  deleteDoc,
  getStorage,
  storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "@/services/legacyBrowserPersistenceBoundary";
import { toast } from "vue3-toastify";
import { isItemAuthorityCommandsFeatureEnabled } from "@/config/runtimeConfig";

import ToggleSwitch from "@/components/GenericComponents/ToggleSwitch.vue";
import HelpHint from "@/components/GenericComponents/HelpHint.vue";
import SetLocation from "@/components/DashboardComponents/EcosystemComponents/SetLocation.vue";
import ImageUpload from "@/components/GenericComponents/ImageUpload.vue";
import PreviewItem from "@/components/DashboardComponents/EcosystemComponents/PreviewItem.vue";

export default {
  name: "AddServiceForm",
  components: {
    ToggleSwitch,
    HelpHint,
    SetLocation,
    ImageUpload,
    PreviewItem,
  },
  props: {
    currentProduct: {
      type: Object,
      required: false,
    },
    user: {
      type: Object,
      required: true,
    },
    userCredentials: {
      type: Object,
      required: false,
    },
    currentStep: {
      type: String,
      required: true,
    },
    userUnitsOrg: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  emits: [
    "update-title",
    "update-canPreview",
    "serviceAddedSuccessfully",
    "update-currentStep",
  ],
  setup(props, { emit }) {
    const store = useStore();

    console.log("userUnitsOrg in service form:", props.userUnitsOrg);

    const itemName = ref("");
    const itemDescription = ref("");
    const price = ref(0);
    const category = ref("Services");
    const local = ref(false);
    const isPublished = ref(false);
    const location = ref(null);
    const showMap = ref(false);

    const highlightName = ref(false);
    const highlightDescription = ref(false);
    const highlightPrice = ref(false);

    const formRef = ref(null);
    const selectedDays = ref([true, true, true, true, true, false, false]); // Weekdays selected by default
    const daysOfWeek = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];
    const abbreviatedDaysOfWeek = [
      "Mon",
      "Tue",
      "Wed",
      "Thu",
      "Fri",
      "Sat",
      "Sun",
    ];
    const hours = Array.from(
      { length: 24 },
      (_, i) => `${i.toString().padStart(2, "0")}:00`
    );
    const customAvailability = ref([]);
    const selectedSlots = ref(new Array(7).fill([]).map(() => []));

    const selectedFiles = ref([]);
    const existingImageUrls = ref([]);
    const thumbnailIndex = ref(0);
    const gatheredServiceData = ref({});

    // New additions for image handling
    const editedImages = ref([]); // Stores objects { oldUrl, newFile }
    const removedImageUrls = ref([]); // Stores URLs of images to delete
    const fileToUrlMap = ref(new Map()); // Maps File objects to their blob URLs

    const categories = reactive([
      "Technology",
      "Fashion & Beauty",
      "Home & Living",
      "Arts & Culture",
      "Business",
      "Health & Wellness",
      "Sports & Outdoors",
      "Transportation",
      "Education",
      "Services",
      "Other",
    ]);

    const isSlotSelected = (dayIndex, hour) => {
      return selectedSlots.value[dayIndex].includes(hour);
    };

    const toggleSlot = (dayIndex, hour) => {
      console.log(`Toggling slot: DayIndex=${dayIndex}, Hour=${hour}`);
      const slots = selectedSlots.value[dayIndex];
      const slotIndex = slots.indexOf(hour);
      if (slotIndex === -1) {
        console.log(`Adding slot: ${hour} to day index ${dayIndex}`);
        // Add slot
        selectedSlots.value[dayIndex] = [...slots, hour];
      } else {
        console.log(`Removing slot: ${hour} from day index ${dayIndex}`);
        // Remove slot
        selectedSlots.value[dayIndex] = [
          ...slots.slice(0, slotIndex),
          ...slots.slice(slotIndex + 1),
        ];
      }
      console.log("Updated selectedSlots:", selectedSlots.value);
    };

    // Updated handleImageUpdates function
    const handleImageUpdates = (
      updatedImages,
      removedUrls,
      editedImagesList,
      imagePreviewUrls // Add this parameter
    ) => {
      console.log("[AddServiceForm] handleImageUpdates called");
      console.log("[AddServiceForm] Before update:", {
        existingImageUrls: existingImageUrls.value,
        selectedFiles: selectedFiles.value,
      });

      // Separate updatedImages into existing URLs and new files
      existingImageUrls.value = updatedImages.filter(
        (image) => typeof image === "string"
      );

      // Get new files and update the fileToUrlMap
      const newFiles = updatedImages.filter((image) => image instanceof File);

      // Update the file to URL mapping using the provided imagePreviewUrls
      if (Array.isArray(imagePreviewUrls) && imagePreviewUrls.length > 0) {
        console.log(
          "[AddServiceForm] Received image preview URLs:",
          imagePreviewUrls
        );

        // First clear the old map to avoid memory leaks
        fileToUrlMap.value.forEach((url) => {
          try {
            URL.revokeObjectURL(url);
          } catch (e) {
            console.warn("[AddServiceForm] Error revoking object URL:", e);
          }
        });
        fileToUrlMap.value.clear();

        // Create new mappings for the files
        let existingUrlCount = existingImageUrls.value.length;
        newFiles.forEach((file, index) => {
          if (index + existingUrlCount < imagePreviewUrls.length) {
            const url = imagePreviewUrls[index + existingUrlCount];
            console.log(
              `[AddServiceForm] Mapping file ${file.name} to URL: ${url}`
            );
            fileToUrlMap.value.set(file, url);
          }
        });
      }

      selectedFiles.value = newFiles;

      // Collect URLs of removed images
      removedImageUrls.value = removedUrls;

      // Collect edited images
      editedImages.value = editedImagesList;

      // Update gatheredServiceData with the updated list of image previews
      gatheredServiceData.value.imagePreviews = imagePreviewUrls;

      console.log("[AddServiceForm] After update:", {
        existingImageUrls: existingImageUrls.value,
        selectedFiles: selectedFiles.value,
        fileToUrlMap: Array.from(fileToUrlMap.value.entries()).map(
          ([file, url]) => ({
            fileName: file.name,
            url: url.substring(0, 50) + "...", // Truncate URL for readability
          })
        ),
      });
    };

    const updateThumbnailIndex = (index) => {
      // Handle when index is a URL string (from ImageUpload component)
      if (typeof index === "string") {
        console.log("[AddServiceForm] Received thumbnail URL:", index);

        // Find the image in our arrays
        let foundIndex = -1;

        // First check in existing image URLs (these should be Firebase storage URLs)
        foundIndex = existingImageUrls.value.findIndex((url) => url === index);
        if (foundIndex !== -1) {
          console.log(
            "[AddServiceForm] Found URL in existingImageUrls at index:",
            foundIndex
          );
          thumbnailIndex.value = foundIndex;
          return;
        }

        // Check in selected files by comparing to their mapped blob URLs
        const fileEntries = Array.from(fileToUrlMap.value.entries());
        const matchingEntry = fileEntries.find(([_, url]) => url === index);

        if (matchingEntry) {
          const [matchedFile] = matchingEntry;
          const fileIndex = selectedFiles.value.findIndex(
            (file) => file === matchedFile
          );

          if (fileIndex !== -1) {
            const thumbnailIdx = existingImageUrls.value.length + fileIndex;
            console.log(
              "[AddServiceForm] Found matching file in selectedFiles at computed index:",
              thumbnailIdx
            );
            thumbnailIndex.value = thumbnailIdx;
            return;
          }
        }

        // If still not found, check any string URLs in selectedFiles
        const selectedFileIndex = selectedFiles.value.findIndex((file) => {
          if (typeof file === "string") {
            return file === index;
          }
          return false;
        });

        if (selectedFileIndex !== -1) {
          console.log(
            "[AddServiceForm] Found URL in selectedFiles at index:",
            selectedFileIndex
          );
          thumbnailIndex.value =
            existingImageUrls.value.length + selectedFileIndex;
          return;
        }

        // As a fallback, use the first image as thumbnail
        console.log(
          "[AddServiceForm] Could not find matching image for URL, using index 0"
        );
        thumbnailIndex.value = 0;
      } else {
        // Handle numeric index as before
        thumbnailIndex.value =
          index !== null && index !== undefined ? index : 0;
      }

      // Ensure the thumbnail index is updated in the gathered data
      console.log(
        `[AddServiceForm] Setting thumbnailIndex to ${thumbnailIndex.value}`
      );
      // Also update the gatheredServiceData to ensure it has the latest thumbnailIndex
      if (gatheredServiceData.value) {
        gatheredServiceData.value.thumbnailIndex = thumbnailIndex.value;
      }
    };

    // Function to get Storage Reference from URL

    // Function to replace an image in Firebase Storage
    const replaceImage = async (oldImageUrl, newFile) => {
      try {
        const storage = getStorage();
        const fileRef = storageRef(storage, oldImageUrl); // Use storageRef
        await uploadBytes(fileRef, newFile);
        const newUrl = await getDownloadURL(fileRef);
        return newUrl;
      } catch (error) {
        console.error(`Failed to replace image at URL: ${oldImageUrl}`, error);
        throw error;
      }
    };

    // Function to delete an image from Firebase Storage using its URL
    const deleteImageByUrl = async (url) => {
      try {
        const fileRef = getStorageRefFromUrl(url);
        await deleteObject(fileRef);
        console.log(`Deleted image at URL: ${url}`);
      } catch (error) {
        console.error(`Failed to delete image at URL: ${url}`, error);
      }
    };

    const handleFormValidation = () => {
      resetHighlights();
      let firstInvalidField = null;
      let errors = [];

      // Global safeguard: block completely empty submissions
      try {
        const noName = !itemName.value?.trim();
        const noDesc = !itemDescription.value?.trim();
        const noImages =
          (selectedFiles.value?.length || 0) === 0 &&
          (existingImageUrls.value?.length || 0) === 0 &&
          !props.currentProduct?.mediaUrl;
        if (noName && noDesc && noImages) {
          toast.error(
            "Please add an image, a title, or a description to continue."
          );
          emit("update-canPreview", false);
          return false;
        }
      } catch {}

      if (!itemName.value.trim()) {
        highlightName.value = true;
        firstInvalidField = firstInvalidField || "itemName";
        errors.push("Service name is required.");
      }

      if (!itemDescription.value.trim()) {
        highlightDescription.value = true;
        firstInvalidField = firstInvalidField || "itemDescription";
        errors.push("Description is required.");
      }

      if (
        price.value === null ||
        price.value === undefined ||
        price.value === ""
      ) {
        highlightPrice.value = true;
        firstInvalidField = firstInvalidField || "price";
        errors.push("Price cannot be empty.");
      } else if (isNaN(parseFloat(price.value))) {
        highlightPrice.value = true;
        firstInvalidField = firstInvalidField || "price";
        errors.push("Please enter a valid number for the price.");
      } else if (parseFloat(price.value) <= 0) {
        highlightPrice.value = true;
        firstInvalidField = firstInvalidField || "price";
        errors.push(
          parseFloat(price.value) < 0
            ? "Price cannot be negative."
            : "Price must be greater than zero."
        );
      }

      if (errors.length > 0) {
        if (firstInvalidField) {
          document.getElementById(firstInvalidField).focus();
          document
            .getElementById(firstInvalidField)
            .scrollIntoView({ behavior: "smooth", block: "center" });
        }
        toast.error(errors.join(" "));
        emit("update-canPreview", false);
        return false;
      } else {
        resetHighlights();
        emit("update-canPreview", true);

        // If we're on the initial step, we should move to the availability step
        if (props.currentStep === "initial") {
          emit("update-currentStep", { form: "service", step: "availability" });
        }
        // If we're on the availability step, we should move to customization
        else if (props.currentStep === "availability") {
          emit("update-currentStep", {
            form: "service",
            step: "customization",
          });
        }
        // If we're on the customization step, we should move to preview
        else if (props.currentStep === "customization") {
          emit("update-currentStep", { form: "service", step: "preview" });
          gatherServiceDataForPreview();
        }

        return true;
      }
    };

    const resetHighlights = () => {
      highlightName.value = false;
      highlightDescription.value = false;
      highlightPrice.value = false;
    };

    const resetForm = () => {
      itemName.value = "";
      itemDescription.value = "";
      price.value = 0;
      category.value = "Services";
      selectedDays.value = [true, true, true, true, true, false, false];
      customAvailability.value = [];
      selectedSlots.value = new Array(7).fill([]).map(() => []);
      resetHighlights();

      // Clean up any object URLs to prevent memory leaks
      fileToUrlMap.value.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          console.warn(
            "[AddServiceForm] Error revoking object URL during reset:",
            e
          );
        }
      });
      fileToUrlMap.value.clear();

      selectedFiles.value = [];
      existingImageUrls.value = [];
      thumbnailIndex.value = 0;
      emit("update-canPreview", false);
    };

    // Updated addOrUpdateService function
    const addOrUpdateService = async () => {
      try {
        if (isItemAuthorityCommandsFeatureEnabled) {
          const error = new Error(
            "Use the private item composer to create or edit services."
          );
          error.code = "private-authority-required";
          throw error;
        }
        if (!handleFormValidation()) {
          return;
        }

        // Handle edited images
        for (const { oldUrl, newFile } of editedImages.value) {
          const newUrl = await replaceImage(oldUrl, newFile);
          // Update existingImageUrls with the new URL
          const index = existingImageUrls.value.indexOf(oldUrl);
          if (index !== -1) {
            existingImageUrls.value[index] = newUrl;
          }
        }

        // Handle removed images
        for (const url of removedImageUrls.value) {
          await deleteImageByUrl(url);
          // Remove the URL from existingImageUrls
          const index = existingImageUrls.value.indexOf(url);
          if (index !== -1) {
            existingImageUrls.value.splice(index, 1);
          }
        }

        // Upload new images
        console.log("Uploading new images");
        const newImageUrls = await uploadNewImages(selectedFiles.value);
        console.log("New Image URLs received:", newImageUrls);

        // Prepare the images array
        const imageUrls = [...existingImageUrls.value, ...newImageUrls];

        // Make sure thumbnailIndex is a number, not a string
        if (typeof thumbnailIndex.value === "string") {
          console.warn(
            "[AddServiceForm] thumbnailIndex is a string, converting to 0:",
            thumbnailIndex.value
          );
          thumbnailIndex.value = 0;
        }

        const serviceData = {
          organisation: organisationUID.value, // Use computed organisationUID
          itemName: itemName.value,
          itemDescription: itemDescription.value,
          price: price.value,
          category: category.value || "Services",
          isPublished: isPublished.value,
          owner: ownerUID.value, // Use computed ownerUID
          createdAt: serverTimestamp(),
          ...(location.value && location.value.lat && location.value.lng
            ? { location: { lat: location.value.lat, lng: location.value.lng } }
            : {}),
          images: imageUrls, // Use the combined image URLs
          thumbnailIndex: Number(thumbnailIndex.value) || 0, // Ensure it's a number
          coinName:
            gatheredServiceData.value.coinName || coinName.value || "Unit",
          coinSymbol:
            gatheredServiceData.value.coinSymbol || coinSymbol.value || "Ʉ",
          type: "service",
        };

        let serviceRef;
        let created = false;
        if (props.currentProduct && props.currentProduct.id) {
          // Update existing service
          serviceRef = doc(null, "services", props.currentProduct.id);
          await setDoc(serviceRef, serviceData, { merge: true });
        } else {
          // Add a new service
          serviceRef = await addDoc(collection(null, "services"), serviceData);
          created = true;
        }

        // Step 1: Fetch existing slots and delete them
        const slotsCollectionRef = collection(serviceRef, "slots");
        const existingSlotsSnapshot = await getDocs(slotsCollectionRef);

        const deleteSlotPromises = existingSlotsSnapshot.docs.map((doc) =>
          deleteDoc(doc.ref)
        );
        await Promise.all(deleteSlotPromises);

        // Step 2: Save the new slots to the subcollection
        const saveSlotsPromises = daysOfWeek.reduce((acc, day, dayIndex) => {
          if (selectedDays.value[dayIndex]) {
            const daySlots = selectedSlots.value[dayIndex]; // Get the slots for that day
            if (daySlots.length > 0) {
              // Save selected time slots for the day
              const dayDocRef = doc(slotsCollectionRef, day); // Day name as the document ID
              const slotsData = {
                day: day,
                slots: daySlots.map((slot) => ({
                  start: slot,
                  end: addOneHour(slot), // 1-hour slots
                })),
              };
              acc.push(setDoc(dayDocRef, slotsData));
            }
          }
          return acc;
        }, []);

        await Promise.all(saveSlotsPromises); // Save all slot data

        // Reset the editedImages and removedImageUrls arrays
        editedImages.value = [];
        removedImageUrls.value = [];

        store.dispatch("updateCoinSymbol", {
          symbol: gatheredServiceData.value.coinSymbol,
          name: gatheredServiceData.value.coinName,
        });
        console.log("Service data being saved:", serviceData);

        const emittedService = {
          id:
            serviceRef.id || (props.currentProduct && props.currentProduct.id),
          itemName: gatheredServiceData.value.itemName || "",
          title: gatheredServiceData.value.itemName || "",
          itemDescription: gatheredServiceData.value.itemDescription || "",
          description: gatheredServiceData.value.itemDescription || "",
          images: Array.isArray(gatheredServiceData.value.images)
            ? gatheredServiceData.value.images
            : gatheredServiceData.value.mediaUrl
            ? [gatheredServiceData.value.mediaUrl]
            : [],
          variants: gatheredServiceData.value.variants || {},
          thumbnailIndex: gatheredServiceData.value.thumbnailIndex || 0,
          mediaUrl: gatheredServiceData.value.mediaUrl || null,
          type: "service",
          _created: created,
        };
        emit("serviceAddedSuccessfully", emittedService);
      } catch (error) {
        console.error("Error adding service: ", error);
        toast.error("Error adding service: " + error.message);
      }
    };

    const uploadNewImages = async (files) => {
      const results = [];
      for (const file of files) {
        if (!(file instanceof File)) continue;
        try {
          const { uploadImageWithVariants } = await import(
            "@/utils/uploadUtils"
          );
          const res = await uploadImageWithVariants(file, {
            folder: `services/${itemName.value}`,
            ownerUid: props.userCredentials?.data?.uid,
          });
          results.push(res.url);
          // If this uploaded image corresponds to the selected thumbnail, capture variants
          try {
            const newIndex =
              existingImageUrls.value.length + (results.length - 1);
            if (
              (Number(thumbnailIndex.value) || 0) === newIndex &&
              res.variants
            ) {
              gatheredServiceData.value = {
                ...gatheredServiceData.value,
                variants: res.variants,
              };
            }
          } catch {}
        } catch (e) {
          console.error(
            "[AddServiceForm] uploadImageWithVariants failed, falling back",
            e
          );
          const storage = getStorage();
          const storagePath = `services/${itemName.value}/${file.name}`;
          const fileRef = storageRef(storage, storagePath);
          await uploadBytes(fileRef, file);
          const url = await getDownloadURL(fileRef);
          results.push(url);
        }
      }
      return results;
    };

    const validatePrice = async () => {
      if (!organisationUID.value) {
        console.error("Organisation UID not found.");
        return;
      }
      highlightPrice.value = false; // Ensure highlight is reset if corrected
      gatherServiceDataForPreview(); // Ensure the preview data is updated after price validation
    };

    const selectDay = (index) => {
      selectedDays.value[index] = !selectedDays.value[index];
    };

    const updateLocation = (newLocation) => {
      location.value = newLocation;
      console.log("Location updated:", location.value); // Add this log for debugging
    };

    const coinSymbol = computed(() => {
      if (!props.userUnitsOrg) {
        // Fall back to Vuex state if userUnitsOrg is not available
        return store.getters.getCoinSymbol || "Ʉ";
      }

      const selectedOrgData =
        props.userUnitsOrg[store.getters.selectedOrgUID];
      // Check if coin_symbol exists in userUnitsOrg, otherwise fall back to Vuex or final default
      return (
        selectedOrgData?.monetary_parameters?.coin_symbol ||
        store.getters.getCoinSymbol ||
        "Ʉ"
      );
    });

    const coinName = computed(() => {
      if (!props.userUnitsOrg) {
        // Fall back to Vuex state if userUnitsOrg is not available
        return store.getters.getCoinName || "Unit";
      }

      const selectedOrgData =
        props.userUnitsOrg[store.getters.selectedOrgUID];
      // Check if coin_name exists in userUnitsOrg, otherwise fall back to Vuex or final default
      return (
        selectedOrgData?.monetary_parameters?.coin_name ||
        store.getters.getCoinName ||
        "Unit"
      );
    });

    const availabilitySummary = computed(() => {
      const regularDays = selectedDays.value
        .map((isSelected, index) => (isSelected ? daysOfWeek[index] : null))
        .filter((day) => day !== null)
        .join(", ");
      const customDays = customAvailability.value.length
        ? "Custom availability set"
        : "No custom availability";
      const alwaysAvailable =
        selectedDays.value.filter((day) => day).length === 0
          ? "Always available"
          : "";
      return `${regularDays}. ${customDays}. ${alwaysAvailable}`;
    });

    const addOneHour = (time) => {
      let [hours, minutes] = time.split(":").map(Number);
      hours += 1;
      if (hours === 24) {
        hours = 23; // Prevent overflow to next day
        minutes = 59; // Set to end of day
      }
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    };

    const getStorageRefFromUrl = (url) => {
      const storage = getStorage();
      const decodedUrl = decodeURIComponent(url);
      const baseUrl = `https://firebasestorage.googleapis.com/v0/b/${storage.app.options.storageBucket}/o/`;
      const path = decodedUrl.replace(baseUrl, "").split("?alt=media")[0];
      return storageRef(storage, path);
    };

    const gatherServiceDataForPreview = () => {
      console.log(
        "DEBUG: gatherServiceDataForPreview CALLED in AddServiceForm"
      );
      console.trace(); // Shows stack trace to find the caller
      console.log("Current state before gathering:", {
        itemName: itemName.value,
        itemDescription: itemDescription.value,
        price: price.value,
        category: category.value,
        existingImageUrls: existingImageUrls.value,
        selectedFiles: selectedFiles.value,
        thumbnailIndex: thumbnailIndex.value,
      });
      const fallbackServiceName = "Unnamed service";
      const fallbackDescription = "No description provided.";
      // Create a combined array of images for preview
      // First, use existing URLs if available
      const imageUrlsForPreview = [...existingImageUrls.value];

      // Then add any new selected files as blob URLs
      if (selectedFiles.value && selectedFiles.value.length > 0) {
        selectedFiles.value.forEach((file) => {
          if (file instanceof File) {
            const blobUrl = URL.createObjectURL(file);
            imageUrlsForPreview.push(blobUrl);
          } else if (typeof file === "string") {
            imageUrlsForPreview.push(file);
          }
        });
      }

      // Now set all the service data
      gatheredServiceData.value = {
        ...gatheredServiceData.value, // Preserve existing properties
        itemName: itemName.value.trim() || fallbackServiceName,
        itemDescription: itemDescription.value.trim() || fallbackDescription,
        price: price.value,
        category: category.value,
        coinName: coinName.value,
        coinSymbol: coinSymbol.value,
        location: location.value
          ? { lat: location.value.lat, lng: location.value.lng }
          : null, // Ensure the object structure
        imagePreviews: imageUrlsForPreview, // Set the image previews for new services
        images: imageUrlsForPreview, // Also set images for PreviewItem component for existing services
        thumbnailIndex: thumbnailIndex.value, // Make sure thumbnailIndex is included
      };

      console.log("Gathered service data:", gatheredServiceData.value);

      emit("update-canPreview", true);
    };

    const fetchSlotsData = async (serviceId) => {
      console.log("Fetching slots data for service ID:", serviceId);
      const slotsCollectionRef = collection(null, `services/${serviceId}/slots`);
      const slotsSnapshot = await getDocs(slotsCollectionRef);
      const fetchedSlots = [];

      slotsSnapshot.forEach((doc) => {
        fetchedSlots.push(doc.data());
      });

      return fetchedSlots;
    };

    const selectedDaysOfWeek = computed(() => {
      return abbreviatedDaysOfWeek.filter(
        (day, index) => selectedDays.value[index]
      );
    });

    const organisationUID = computed(() => {
      // If currentProduct (which is your service in this context) exists and has an organisation set
      if (props.currentProduct && props.currentProduct.organisation) {
        return props.currentProduct.organisation;
      }

      return store.getters.selectedOrgUID || null;
    });

    const ownerUID = computed(() => {
      // If currentProduct (service) exists and has an owner set
      if (props.currentProduct && props.currentProduct.owner) {
        return props.currentProduct.owner;
      }

      // If currentProduct does not have an owner, fall back to userCredentials
      return props.userCredentials?.data?.uid || null;
    });

    watch(
      () => props.currentStep,
      (newStep) => {
        console.log(`[AddServiceForm] Current step changed to: ${newStep}`);

        if (newStep === "initial") {
          // Set title based on whether we're editing an existing service or adding new one
          if (props.currentProduct && props.currentProduct.id) {
            emit("update-title", "Edit Service Details");
          } else {
            emit("update-title", "Add Service");
          }
        } else if (newStep === "availability") {
          // Emit the step update to ensure parent components are aware
          emit("update-currentStep", { form: "service", step: "availability" });
          emit("update-title", "Service Availability");
        } else if (newStep === "customization") {
          // Emit the step update to ensure parent components are aware
          emit("update-currentStep", {
            form: "service",
            step: "customization",
          });
          emit("update-title", "Service Customization");
        } else if (newStep === "preview") {
          emit("update-currentStep", { form: "service", step: "preview" });
          emit("update-title", "Preview");
          gatherServiceDataForPreview();
        } else if (newStep === "delete") {
          // Allow delete step without showing validation errors
          emit("update-currentStep", { form: "service", step: "delete" });
          emit("update-title", "Delete Confirmation");
        }
      }
    );

    // Watch for changes in currentProduct to update form title if needed
    watch(
      () => props.currentProduct,
      (newProduct) => {
        if (newProduct && Object.keys(newProduct).length > 0 && newProduct.id) {
          console.log(
            "[AddServiceForm] Editing existing service:",
            newProduct.id
          );
          // Set title for editing if we're on the initial step
          if (props.currentStep === "initial") {
            emit("update-title", "Edit Service Details");
          }
        } else if (props.currentStep === "initial") {
          // Set title for adding new service
          emit("update-title", "Add Service");
        }
      },
      { immediate: true } // Run immediately on component creation
    );

    watch(price, (newValue, oldValue) => {
      validatePrice();
      gatherServiceDataForPreview();
    });

    watch(
      [itemName, itemDescription, price, selectedFiles, existingImageUrls],
      gatherServiceDataForPreview,
      { immediate: true }
    );

    // Watch for changes in the currentProduct prop and update form fields accordingly
    watch(
      () => props.currentProduct,
      async (newProduct) => {
        if (newProduct && newProduct.id) {
          console.log("Initializing form with currentService:", newProduct);

          itemName.value = newProduct.itemName || "";
          itemDescription.value = newProduct.itemDescription || "";
          price.value = newProduct.price || 0;
          category.value = newProduct.category || "Services";
          isPublished.value = newProduct.isPublished || false;

          // Pre-populate the location and show map if location exists
          if (newProduct.location) {
            location.value = {
              lat: newProduct.location.lat,
              lng: newProduct.location.lng,
            }; // Update to read as an object
            showMap.value = true; // Automatically toggle map visibility if location exists
          } else {
            location.value = null;
            showMap.value = false;
          }

          // Clear selected files when editing a service
          selectedFiles.value = [];

          // Set existing image URLs
          const images = newProduct.images || [];
          existingImageUrls.value = Array.isArray(images) ? images : [images];
          console.log("Set existing service images:", existingImageUrls.value);

          // Make sure thumbnailIndex is a number
          const thumbnailIdx = Number(newProduct.thumbnailIndex);
          thumbnailIndex.value = !isNaN(thumbnailIdx) ? thumbnailIdx : 0;
          console.log(
            `[AddServiceForm] Initialized thumbnailIndex to ${thumbnailIndex.value}`
          );

          // Make sure gatheredServiceData is updated with the thumbnailIndex
          if (!gatheredServiceData.value) {
            gatheredServiceData.value = {};
          }
          gatheredServiceData.value.thumbnailIndex = thumbnailIndex.value;

          // Fetch and update slots data
          const slotsData = await fetchSlotsData(newProduct.id);
          selectedSlots.value = new Array(7).fill([]).map(() => []);

          slotsData.forEach((slot) => {
            const dayIndex = daysOfWeek.indexOf(slot.day);
            if (dayIndex !== -1) {
              selectedSlots.value[dayIndex] = slot.slots.map((s) => s.start);
            }
          });

          gatherServiceDataForPreview();
        } else {
          resetForm();
        }
      },
      { immediate: true }
    );

    // Clean up resources when component is unmounted
    onBeforeUnmount(() => {
      // Revoke all object URLs to prevent memory leaks
      fileToUrlMap.value.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          console.warn(
            "[AddServiceForm] Error revoking object URL during unmount:",
            e
          );
        }
      });
      fileToUrlMap.value.clear();
    });

    return {
      itemName,
      itemDescription,
      price,
      category,
      local,
      isPublished,
      categories,
      highlightName,
      highlightDescription,
      highlightPrice,
      formRef,
      handleFormValidation,
      addOrUpdateService,
      validatePrice,
      selectDay,
      selectedDays,
      daysOfWeek,
      abbreviatedDaysOfWeek,
      customAvailability,
      selectedDaysOfWeek,
      hours,
      isSlotSelected,
      toggleSlot,
      coinName,
      coinSymbol,
      resetForm,
      availabilitySummary,
      addOneHour,
      showMap,
      location,
      updateLocation,
      selectedFiles,
      existingImageUrls,
      handleImageUpdates,
      updateThumbnailIndex,
      thumbnailIndex,
      gatherServiceDataForPreview,
      gatheredServiceData,
      organisationUID,
    };
  },
};
</script>

<style scoped>
.highlight-input {
  transition: background-color 0.3s ease-in-out;
  background-color: var(--color-feedback-danger-surface-strong);
}

.addServiceForm {
  width: 100%;
}

.form-group {
  margin-bottom: 1rem;
}

.form-control {
  width: 100%;
}

.selected-day {
  background-color: var(--color-feedback-success-surface-subtle);
}

table.selectable td,
table.selectable th {
  text-align: center !important;
}

.selected-slot.innerSticker {
  background-color: var(--color-accent-selection);
  color: var(--color-text-on-accent-selection);
  width: initial !important;
  padding: initial !important;
  height: var(--control-height-default);
}

.weekday-container,
.weekend-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-1);
  margin-bottom: var(--space-5);
}
</style>
